<template>
  <div
      class="input-field"
      :class="{
        'readonly': readonly,
        'disabled': disabled,
        'valid': !showLoader && !hasError && showValidCheck,
        'loading': showLoader,
        'icon-right': showIconDate || showLoader,
        'filled': filled,
        'focused': focused,
        'label-hidden': hideLabel,
        [type]: true,
      }"
      :style="cssVariables"
      @click="input.$el ? input.$el.focus({ preventScroll: true }) : false"
  >
    <div class="col-main">
      <div>
        <label
            :for="componentId"
            v-html="label"
        ></label>
        <component
            :is="componentType"
            :id="componentId"
            :readonly="readonly"
            :disabled="disabled"
            :min-date="minDate"
            :max-date="maxDate"
            ref="input"
            :disable-weekend="disableWeekend"
            :value="modelValue"
            @keydown="componentError = []"
            @update:modelValue="onInput"
            @blur="onBlur"
            @focusin="focused = true"
            @focusout="focused = false"
            @paste="onPaste"
            @drop="onPaste"
        />
      </div>
    </div>
    <div
        v-if="showUnit"
        class="col-unit-right"
    >
      <span class="input-unit--right">
        {{ unit }}
      </span>
    </div>
    <div
        v-if="showIconDate || showLoader"
        class="col-icon-right"
        :class="{ 'loader': showLoader }"
    >
      <span class="input-icon--right">
        <FontAwesomeIcon
            v-if="showIconDate"
            icon="fa-regular fa-calendar-days"
        />
        <FontAwesomeIcon
            v-else
            class="fa-pulse"
            icon="fa-solid fa-spinner"
        />
      </span>
    </div>
    <div
        v-if="showValidCheck || showInvalidCross"
        class="col-check"
    >
      <div
          v-if="showValidCheck"
          class="valid-check"
          :style="{
            background: validCheckColor,
          }"
      >
        <div class="icon">
          <FontAwesomeIcon icon="fa-solid fa-check"/>
        </div>
      </div>
      <Tooltip
          v-else
          class="invalid-check"
          is-error
      >
        <div class="icon">
          <FontAwesomeIcon icon="fa-solid fa-exclamation"/>
        </div>
        <template #title>
          <span v-html="errorMessage"></span>
        </template>
      </Tooltip>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Tooltip from '@/components/Tooltip.vue';
import {
  computed,
  ref,
  onMounted,
  watch,
} from 'vue';
import TextField from '@/components/inputs/TextField.vue';
import TextareaField from '@/components/inputs/TextareaField.vue';
import NumberField from '@/components/inputs/NumberField.vue';
import DateField from '@/components/inputs/DateField.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faSpinner,
  faCheck,
  faExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faCalendarDays,
} from '@fortawesome/pro-regular-svg-icons';

library.add(faCalendarDays, faSpinner, faCheck, faExclamation);

const emit = defineEmits(['input']);

const modelValue = defineModel();
const error = defineModel<undefined | string | string[]>('error');
const props = withDefaults(defineProps<{
  type?: string
  name?: string
  label: string
  unit?: string
  readonly?: boolean
  disabled?: boolean
  minDate?: string | number | Date
  maxDate?: string | number | Date
  minLength?: number
  maxLength?: number
  min?: number
  max?: number
  onlyNumeric?: boolean
  errorNumeric?: string
  showLoader?: boolean
  isValid?: boolean
  validColor?: string | Array<string | undefined>
  showValidCheck?: boolean
  disableWeekend?: boolean
  hideLabel?: boolean
  preventPaste?: boolean
}>(), {
  type: 'text',
});

const componentId = `if-${Math.random().toString(36).substring(2, 11)}`;
const componentError = ref<string | string[]>();
watch(() => error.value, () => {
  componentError.value = error.value;
}, {
  immediate: true,
  deep: true,
});
watch(() => modelValue.value, () => {
  error.value = undefined;
}, {
  deep: true,
});

const componentType = computed(() => {
  switch (props.type) {
    case 'date':
      return DateField;
    case 'number':
      return NumberField;
    case 'textarea':
      return TextareaField;
    default:
      return TextField;
  }
});

const input = ref();

const errorMessage = computed<string | undefined>(() => {
  if (Array.isArray(componentError.value)) {
    if (componentError.value[0]) {
      return `${componentError.value[0]}`.trim();
    }
  } else if (componentError.value) {
    return `${componentError.value}`.trim();
  }

  return undefined;
});
const hasError = computed<boolean>(() => errorMessage.value !== undefined);

const onPaste = (e: Event) => {
  if (props.preventPaste) {
    e.preventDefault();
  }
};

const emitInput = ($value: unknown) => {
  emit('input', $value);
};

const onInput = ($value: string | number) => {
  let formattedVal = $value;

  if (typeof formattedVal === 'string') {
    if (props.onlyNumeric) {
      formattedVal = formattedVal.replace(/\D/g, '');
    }

    if (props.minLength !== undefined && formattedVal.length < props.minLength) {
      componentError.value = `Mindestens ${props.minLength} Zeichen`;
      if (props.onlyNumeric) {
        componentError.value = props.errorNumeric || `Zeichenfolge aus mindestens ${props.minLength} Zahlen`;
      }
    } else if (props.maxLength !== undefined && formattedVal.length > props.maxLength) {
      formattedVal = formattedVal.substring(0, props.maxLength);
      if (input.value) {
        input.value.$el.value = formattedVal;
      }
    }
    if (props.minLength !== undefined && formattedVal.length >= props.minLength) {
      componentError.value = [];
    }
  }

  modelValue.value = formattedVal;
  emitInput(formattedVal);
};

const onBlur = () => {
  let formattedVal = modelValue.value;

  if (typeof formattedVal === 'string') {
    if (props.onlyNumeric) {
      let stringVal = formattedVal as string;

      stringVal = stringVal.replace(/\D/g, '');

      const numberValue = parseInt(stringVal, 10);

      if (input.value) {
        if (props.min !== undefined && numberValue < props.min) {
          formattedVal = `${props.min}`;
        }

        if (props.max !== undefined && numberValue > props.max) {
          formattedVal = `${props.max}`;
        }

        input.value.$el.value = formattedVal;
      } else if (props.min !== undefined) {
        input.value.$el.value = formattedVal;
      }
    }
  } else if (formattedVal !== undefined && props.type === 'number' && input.value) {
    const numberValue = formattedVal as number;

    if (props.min !== undefined && numberValue < props.min) {
      formattedVal = props.min;
    }

    if (props.max !== undefined && numberValue > props.max) {
      formattedVal = props.max;
    }

    input.value.$el.value = formattedVal;
  }

  modelValue.value = formattedVal;
  emitInput(formattedVal);
};

const focused = ref(false);
const filled = computed(() => `${typeof modelValue.value === 'number' ? modelValue.value : modelValue.value || ''}`.trim() !== '');
const showIconDate = computed(() => props.type === 'date');
const showUnit = computed(() => !showIconDate.value && props.unit);
const showValidCheck = computed(() => props.isValid);
const validCheckColor = computed<string>(() => {
  let colors = Array.isArray(props.validColor) ? props.validColor : [props.validColor];
  colors = colors.filter((color) => !!color).map((color) => `${color}`.trim());

  if (colors.length >= 2) {
    return `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
  }

  return colors[0];
});

const showInvalidCross = computed(() => errorMessage.value !== undefined && !showValidCheck.value);

interface GridItems {
  main: string
  unit?: string,
  iconRight?: string
  check?: string
}
const inputGrid = computed<{
  items: GridItems
  columns: string[]
}>(() => {
  const columns = [];
  const items: GridItems = {
    main: '1 / 2',
  };

  columns.push('1fr');
  items.main = `${columns.length} / ${columns.length + 1}`;

  if (showUnit.value) {
    columns.push('auto');
    items.unit = `${columns.length} / ${columns.length + 1}`;
  }

  if (props.showLoader || showIconDate.value) {
    columns.push('auto');
    items.iconRight = `${columns.length} / ${columns.length + 1}`;
  }

  if (showValidCheck.value || showInvalidCross.value) {
    columns.push('auto');
    items.check = `${columns.length} / ${columns.length + 1}`;
  }

  return {
    items,
    columns,
  };
});

const cssVariables = computed<Record<string, string | undefined>>(() => ({
  '--grid-columns': inputGrid.value.columns.join(' '),
  '--grid-column-main': inputGrid.value.items.main,
  '--grid-column-check': inputGrid.value.items.check,
  '--grid-column-unit': inputGrid.value.items.unit,
  '--grid-column-unit-padding': (inputGrid.value.items.check || inputGrid.value.items.iconRight) ? '12px' : '1.875em',
  '--grid-column-icon-right': inputGrid.value.items.iconRight,
  '--grid-column-icon-right-padding': inputGrid.value.items.check ? '12px' : '1.875em',
}));

onMounted(() => {
  emitInput(modelValue.value);
});
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.input-field {
  @include font-regular(16px);
  font-weight: 700;
  display: grid;
  grid-template-rows: 100%;
  grid-auto-columns: var(--grid-columns);
  color: #38383A;
  line-height: 1.25;
  background-color: #FFFFFF;
  align-items: center;
  padding: .6em 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 1.625em !important;

  .input-icon {
    &--left, &--right {
      line-height: .1;

      &.loader {
        @include font-regular(26px);
      }
    }
  }

  .col-icon-left {
    grid-column: var(--grid-column-icon-left);
    align-self: center;
    padding: 0 15px;

    > img {
      display: block;
      height: 1.625rem;
      width: auto;
    }

    & + .col-main {
      padding-left: 0;
    }
  }

  .col-main {
    grid-column: var(--grid-column-main);
    align-self: center;
    padding: 0 1.875em;

    > div {
      position: relative;
    }
  }

  .col-chevron {
    grid-column: var(--grid-column-chevron);
    padding-right: var(--grid-column-chevron-padding);
    line-height: 0;
  }

  .col-unit-right {
    grid-column: var(--grid-column-unit);
    padding-right: var(--grid-column-unit-padding);
    line-height: 0;
  }

  .col-icon-right {
    grid-column: var(--grid-column-icon-right);
    padding-right: var(--grid-column-icon-right-padding);
    line-height: 0;
  }

  .col-loader {
    grid-column: var(--grid-column-loader);
    padding-right: 12px;
    line-height: 0;
  }

  .col-check {
    grid-column: var(--grid-column-check);
    line-height: 0;
    padding-right: 12px;

    .valid-check, .invalid-check {
      height: 1.875em;
      width: 1.875em;
      position: relative;
      background: $primary;
      border-radius: 1.875em;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

      > .icon {
        position: absolute;
        display: inline-block;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
        color: #FFF;
      }
    }

    .invalid-check {
      background: $error-gradient;
    }
  }

  &.textarea .col-check {
    align-self: start;
  }

  label {
    color: inherit;
    display: block;
    pointer-events: none;
    transition: font-size .275s ease-in-out, top .275s ease-in-out;
    position: absolute;
    top: .5em;
    font-weight: 300;
    transform: translateX(0) translateY(0) translateZ(0);
    left: 0;
    will-change: top, font-size;
    -webkit-font-smoothing: subpixel-antialiased;
    backface-visibility: hidden;
    white-space: nowrap;
  }

  &.focused, &.filled {
    label {
      font-size: 10px;
      top: 0;
    }
  }

  input, textarea, .input-label {
    border: 0;
    padding: .8em 0 0;
    margin: 0 0 -0.125em 0;
    outline: none;
    color: inherit;
    width: 100%;
    background: transparent;
    line-height: 1.25;
    font-weight: 700;
    min-height: 2.3625em;
  }

  &.readonly {
    input, textarea, .input-label {
      color: #7C7C7C;
    }
  }

  &.disabled {
    background-color: #E7F1F5;

    > * {
      opacity: .5;
    }
  }

  .input-label + input {
    position: absolute;
    opacity: 0;
  }

  &.label-hidden {
    input, textarea, .input-label {
      padding-top: calc(.65em - 10px);
    }
  }

  &.readonly {
    pointer-events: none;

    input, textarea {
      color: #7C7C7C;
    }
  }

  textarea {
    line-height: 1.6;
    resize: none;
  }

  &.icon-left {
    > .col-main {
      border-left: 1px solid #DBDBDB;
    }

    > div {
      &:nth-child(1) {
        padding-right: 1em;
        padding-left: 0;
      }

      &.col-main {
        padding-left: 1em;
      }
    }
  }

  &.icon-right, &.input-field--select {
    > .col-main {
      padding-right: 1em;
    }

    > .col-icon {
      padding-left: 1em;
      padding-right: 0;
    }
  }

  &--select {
    position: relative;

    .input-icon--dropdown {
      transform: rotateZ(0deg);
      transition: transform .275s ease;
      display: inline-block;
    }
  }
}

.select-field-wrapper {
  &.open {
    .input-field {
      border-bottom-left-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }

    .input-icon--dropdown {
      transform: rotateZ(-180deg);
    }
  }
}
</style>
